<template>
  <div class="mover-plantas-evento container-fluid mt-4">
    <h2>Mover Plantas al Evento</h2>
    
    <!-- Filtros -->
    <div class="filters my-3 d-flex flex-wrap gap-2 align-items-center">
      <input
        type="text"
        v-model="filterText"
        class="form-control form-control-sm"
        placeholder="Filtrar por planta..."
        style="max-width: 200px;"
      />
      <select v-model="selectedLoteFilter" class="form-select form-select-sm" style="max-width: 180px;">
        <option value="">Todos los lotes</option>
        <option
          v-for="lote in uniqueLotes"
          :key="lote.id"
          :value="lote.id"
        >
          {{ lote.nombre }}
        </option>
      </select>
      <button class="btn btn-secondary btn-sm" @click="clearFilters">Limpiar Filtros</button>
    </div>
    
    <!-- Acordeón manual -->
    <div v-if="groupedLotes.length > 0">
      <div class="accordion" id="lotesAccordion">
        <div 
          v-for="grupo in paginatedLoteGroups" 
          :key="grupo.lote.id" 
          class="accordion-item mb-2"
        >
          <h2 class="accordion-header" :id="'heading-' + grupo.lote.id">
            <div class="d-flex w-100 align-items-center justify-content-between">
              <button 
                class="accordion-button" 
                :class="{ collapsed: !openPanels[grupo.lote.id] }"
                type="button" 
                @click="toggleCollapse(grupo.lote.id)"
              >
                Lote: {{ grupo.lote.nombre }}
                <span class="ms-2 badge bg-info">
                  {{ grupo.plantas.length }} plantas
                </span>
                <font-awesome-icon
                  :icon="['fas', openPanels[grupo.lote.id] ? 'chevron-up' : 'chevron-down']"
                  class="ms-2"
                />
              </button>
              <button class="btn btn-sm btn-outline-info ms-2" @click.stop="toggleLotSelection(grupo)">
                {{ isLotSelected(grupo) ? 'Deseleccionar' : 'Seleccionar' }} lote
              </button>
            </div>
            <div class="mt-1 text-end small text-muted">
              Seleccionadas: {{ selectedCount(grupo) }}/{{ grupo.plantas.length }}
            </div>
          </h2>

          <div
            :id="'collapse-' + grupo.lote.id"
            class="accordion-collapse"
            :class="openPanels[grupo.lote.id] ? 'collapse show' : 'collapse'"
            :aria-labelledby="'heading-' + grupo.lote.id"
          >
            <div class="accordion-body p-2">
              <div 
                v-for="planta in grupo.plantas" 
                :key="planta.id" 
                class="plant-item card p-2 mb-1"
                :class="{ selected: selectedPlantas.includes(planta.id) }"
                @click="togglePlantaSelection(planta)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ planta.codigo }}</strong>
                    <br>
                    <small>ID: {{ planta.id }}</small>
                  </div>
                  <div>
                    <small class="text-muted">
                      Asignación: {{ formatDate(planta.fecha_asignacion) }}
                    </small>
                    <br>
                    <small class="text-muted">
                      Salida: {{ formatDate(planta.fecha_salida) || 'N/A' }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginación de grupos -->
      <nav v-if="totalGroupPages > 1" class="mt-2">
        <ul class="pagination pagination-sm justify-content-center">
          <li class="page-item" :class="{ disabled: currentGroupPage === 1 }">
            <a class="page-link" href="#" @click.prevent="prevGroupPage">Anterior</a>
          </li>
          <li 
            v-for="page in visibleGroupPages" 
            :key="page" 
            class="page-item" 
            :class="{ active: currentGroupPage === page }"
          >
            <a class="page-link" href="#" @click.prevent="currentGroupPage = page">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: currentGroupPage === totalGroupPages }">
            <a class="page-link" href="#" @click.prevent="nextGroupPage">Siguiente</a>
          </li>
        </ul>
      </nav>
    </div>
    <div v-else class="text-center text-muted">
      No se encontraron plantas.
    </div>
    
    <!-- Confirmación -->
    <div class="d-flex flex-column align-items-end mt-4">
      <div class="mb-2">
        <strong>Plantas seleccionadas:</strong> {{ selectedPlantas.length }}
      </div>
      <button
        class="btn btn-primary"
        @click="confirmarMovimiento"
        :disabled="selectedPlantas.length === 0 || proyecto_cerrado || evento_finalizado"
      >
        Mover Máximo 200 Plantas al Evento
      </button>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'
import { mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'MoverPlantasEvento',
  components: { FontAwesomeIcon },
  props: {
    proyecto_evento_id: { type: Number, required: true },
    proyecto_id: { type: Number, required: true }
  },
  data() {
    return {
      eligiblePlantas: [],
      loading: false,
      filterText: '',
      selectedLoteFilter: '',
      currentGroupPage: 1,
      groupPageSize: 5,
      selectedPlantas: [],
      proyecto_cerrado: false,
      evento_finalizado: false,
      openPanels: {}
    }
  },
  computed: {
    ...mapGetters(['getClubActual']),
    filteredPlantas() {
      let list = this.eligiblePlantas
      if (this.filterText) {
        list = list.filter(p => p.codigo.toLowerCase().includes(this.filterText.toLowerCase()))
      }
      if (this.selectedLoteFilter) {
        list = list.filter(p => p.lote && p.lote.id === this.selectedLoteFilter)
      }
      return list
    },
    groupedLotes() {
      const groups = {}
      this.filteredPlantas.forEach(p => {
        if (p.lote) {
          if (!groups[p.lote.id]) groups[p.lote.id] = { lote: p.lote, plantas: [] }
          groups[p.lote.id].plantas.push(p)
        }
      })
      return Object.values(groups)
    },
    paginatedLoteGroups() {
      const start = (this.currentGroupPage - 1) * this.groupPageSize
      return this.groupedLotes.slice(start, start + this.groupPageSize)
    },
    totalGroupPages() {
      return Math.ceil(this.groupedLotes.length / this.groupPageSize)
    },
    visibleGroupPages() {
      const total = this.totalGroupPages
      let start = Math.max(1, this.currentGroupPage - 2)
      let end = Math.min(total, start + 4)
      if (end - start < 4) start = Math.max(1, end - 4)
      return Array.from({ length: end - start + 1 }, (_, i) => i + start)
    },
    uniqueLotes() {
      const lotes = {}
      this.eligiblePlantas.forEach(p => { if (p.lote) lotes[p.lote.id] = p.lote })
      return Object.values(lotes)
    }
  },
  mounted() {
    this.proyecto_cerrado = this.$store.getters.getProyectoActual.proyecto_cerrado
    this.evento_finalizado = this.$store.getters.getEventoActual.evento_finalizado
    this.fetchEligiblePlantas()
  },
  methods: {
    toggleCollapse(id) {
      // Vue 3 reactivity permite asignar directamente
      this.openPanels[id] = !this.openPanels[id]
    },
    async fetchEligiblePlantas() {
      this.loading = true
      try {
        const club_id = this.getClubActual.id
        const res = await axios.get('/api/plantas_disponibles', { params: { club_id, proyecto_id: this.proyecto_id, proyecto_evento_id: this.proyecto_evento_id } })
        this.eligiblePlantas = res.data
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    togglePlantaSelection(planta) {
      const idx = this.selectedPlantas.indexOf(planta.id)
      if (idx === -1) {
        if (this.selectedPlantas.length < 200) this.selectedPlantas.push(planta.id)
        else alert('Solo se pueden mover hasta 200 plantas a la vez.')
      } else this.selectedPlantas.splice(idx, 1)
    },
    clearFilters() {
      this.filterText = ''
      this.selectedLoteFilter = ''
      this.currentGroupPage = 1
    },
    prevGroupPage() { if (this.currentGroupPage > 1) this.currentGroupPage-- },
    nextGroupPage() { if (this.currentGroupPage < this.totalGroupPages) this.currentGroupPage++ },
    toggleLotSelection(grupo) {
      if (this.isLotSelected(grupo)) {
        grupo.plantas.forEach(p => {
          const idx = this.selectedPlantas.indexOf(p.id)
          if (idx !== -1) this.selectedPlantas.splice(idx, 1)
        })
      } else {
        grupo.plantas.forEach(p => {
          if (!this.selectedPlantas.includes(p.id) && this.selectedPlantas.length < 200) {
            this.selectedPlantas.push(p.id)
          }
        })
      }
    },
    isLotSelected(grupo) {
      return grupo.plantas.every(p => this.selectedPlantas.includes(p.id))
    },
    selectedCount(grupo) {
      return grupo.plantas.filter(p => this.selectedPlantas.includes(p.id)).length
    },
    async confirmarMovimiento() {
      if (!this.selectedPlantas.length) return
      if (this.selectedPlantas.length > 200) return alert('Solo 200 plantas max.')
      if (!confirm(`¿Mover ${this.selectedPlantas.length} plantas?`)) return
      try {
        const payload = {
          proyecto_evento_id: this.proyecto_evento_id,
          proyecto_id: this.proyecto_id,
          plantas_ids: this.selectedPlantas,
          fecha_inicio: new Date().toISOString().split('T')[0]
        }
        await axios.post('/api/planta_eventos', payload)
        alert('Listo!')
        this.selectedPlantas = []
        this.fetchEligiblePlantas()
      } catch (e) {
        console.error(e)
        alert('Error al mover plantas')
      }
    },
    formatDate(dateStr) {
      return dateStr ? new Date(dateStr).toLocaleDateString() : ''
    }
  }
}
</script>


<style scoped>
.mover-plantas-evento {
  padding: 15px;
  background-color: #f7f8fa;
  font-size: 0.9rem;
}
.filters input,
.filters select {
  margin-bottom: 5px;
}
.accordion-button {
  font-size: 0.9rem;
  padding: 6px;
}
.accordion-body {
  font-size: 0.85rem;
  padding: 4px;
}
.plant-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 6px;
  border-radius: 4px;
  border: 1px solid #e5e5e5;
}
.plant-item.selected {
  background-color: #d1ecf1;
}
.pagination {
  margin-top: 10px;
}
@media (max-width: 576px) {
  .btn {
    font-size: 0.75rem;
    padding: 4px 8px;
  }
}
</style>
